import Header from '@components/common/layout/Header';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, {useMemo, useState} from 'react';
import WorkspaceUsageReversalModal from '@components/accounts/WorkspaceUsageReversalModal';
import routePath, {Routes} from '@account/utils/route';
import {ACCOUNT_MANAGER_URL} from '@account/config';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  EntitlementStatus,
  normalizeStatus,
  statusColorMap,
} from '@account/utils/entitlements';
import {ProductAccessStates} from '@account/constants/products';
import {displayDate} from '@account/utils/dates';
import {getWorkspacesForCA} from '@account/api/products';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {renderQuota} from '@account/utils/quota';
import {useAuth} from '@apps-common/auth-react';
import {useCustomerAccount} from '@hooks/data/ims/useCustomerAccount';
import {useFlags} from '@hooks/useFlags';
import {useParams} from 'react-router-dom';
import {useProductAccesses} from '@hooks/data/products/useProductAccesses';
import {useQuery} from '@tanstack/react-query';

// Shared with the CA Workspaces tab: map a raw PA state to a display label +
// MUI chip color, and treat active/pending as "live".
const PA_STATUS_LABELS = {
  [EntitlementStatus.ACTIVE]: 'Active',
  [EntitlementStatus.PENDING]: 'Pending',
  [EntitlementStatus.EXPIRED]: 'Expired',
  [EntitlementStatus.INACTIVE]: 'Inactive',
  [EntitlementStatus.ERROR]: 'Error',
};
const paStatus = state => {
  const status = normalizeStatus(state);
  return {
    label: PA_STATUS_LABELS[status] ?? status,
    color: statusColorMap[status],
  };
};
const isActivePa = pa =>
  ['active', 'pending'].includes((pa.state || '').toLowerCase());

const WorkspaceDetailPage = () => {
  const {accountId, workspaceId} = useParams();
  const auth = useAuth();
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);
  const {showUsageReversal} = useFlags();

  // Row action menu + Usage Reversal modal state.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPa, setMenuPa] = useState(null);
  const [reversalPa, setReversalPa] = useState(null);
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPa(null);
  };

  const accountQuery = useCustomerAccount(accountId, {
    keepPreviousData: true,
    enabled: isPlanetAdmin && !!accountId,
  });
  const accountName = accountQuery.data?.name;

  // Workspace metadata (name) comes from the CA's workspace list — there is no
  // single-workspace endpoint. Cheap and usually already warm from the CA page.
  const workspacesQuery = useQuery({
    queryKey: ['workspaces-for-ca', accountId],
    queryFn: () => getWorkspacesForCA(accountId),
    enabled: isPlanetAdmin && !!accountId,
  });
  const workspace = useMemo(
    () =>
      (workspacesQuery.data?.workspaces ?? []).find(
        w => w.workspace_id === workspaceId
      ),
    [workspacesQuery.data, workspaceId]
  );
  const workspaceName = workspace?.name || 'Workspace';

  const {data: paData, isLoading: pasLoading} = useProductAccesses(
    {
      customerAccountId: accountId,
      workspaceId,
      limit: 200,
      state: [
        ProductAccessStates.ACTIVE,
        ProductAccessStates.PENDING,
        ProductAccessStates.INACTIVE,
        ProductAccessStates.DEACTIVATED,
        ProductAccessStates.EXPIRED,
      ],
    },
    {enabled: isPlanetAdmin && !!accountId && !!workspaceId}
  );
  const productAccesses = paData?.results ?? [];

  const breadcrumbs = useMemo(
    () => [
      {label: 'All Accounts', url: Routes.ACCOUNTS},
      {
        label: accountName ?? '...',
        url: routePath(Routes.ACCOUNT_DETAIL, {accountId}),
      },
      {label: workspaceName},
    ],
    [accountName, accountId, workspaceName]
  );

  if (!isPlanetAdmin) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">
          You do not have permission to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{paddingBottom: 8}}>
      <Header
        name={workspaceName}
        breadcrumbs={breadcrumbs}
        helmetTitle={workspaceName}
      >
        <Button
          variant="outlined"
          size="small"
          component="a"
          href={`${ACCOUNT_MANAGER_URL}#/workspaces/${workspaceId}/info`}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon fontSize="small" />}
        >
          Open in Account Manager
        </Button>
      </Header>

      <Box sx={{mt: 2}}>
        <Typography variant="caption" color="text.secondary">
          Workspace ID
        </Typography>
        <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
          {workspaceId}
        </Typography>
      </Box>

      <Box sx={{mt: 4}}>
        <Typography variant="subtitle1" fontWeight={700} sx={{mb: 1}}>
          Products
        </Typography>
        {pasLoading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{p: 2}}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading products…
            </Typography>
          </Stack>
        ) : productAccesses.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
            No products allocated to this workspace.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>Term</TableCell>
                {showUsageReversal && (
                  <TableCell align="right">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {productAccesses.map(pa => {
                const {label: statusLabel, color: statusColor} = paStatus(
                  pa.state
                );
                const muted = !isActivePa(pa);
                return (
                  <TableRow key={pa.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={muted ? 'text.secondary' : undefined}
                      >
                        {pa.title || pa.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel}
                        size="small"
                        variant="outlined"
                        color={statusColor}
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          ...(statusColor
                            ? {}
                            : {
                                color: 'text.secondary',
                                borderColor: 'divider',
                              }),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={muted ? 'text.secondary' : undefined}
                      >
                        {pa.unlimited_quota
                          ? 'Unlimited'
                          : pa.quota_total === null ||
                              pa.quota_total === undefined
                            ? 'N/A'
                            : renderQuota(
                                pa.quota_total - (pa.quota_used ?? 0),
                                pa.quota_units
                              )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={muted ? 'text.secondary' : undefined}
                      >
                        {pa.term_start_date
                          ? `${displayDate(pa.term_start_date)} — ${displayDate(
                              pa.term_end_date
                            )}`
                          : '—'}
                      </Typography>
                    </TableCell>
                    {showUsageReversal && (
                      <TableCell align="right">
                        {pa.billable_id ? (
                          <IconButton
                            size="small"
                            aria-label="Product actions"
                            onClick={e => {
                              setMenuAnchor(e.currentTarget);
                              setMenuPa(pa);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            setReversalPa(menuPa);
            closeMenu();
          }}
        >
          Usage Reversal
        </MenuItem>
      </Menu>

      {reversalPa && (
        <WorkspaceUsageReversalModal
          open={Boolean(reversalPa)}
          onClose={() => setReversalPa(null)}
          productAccess={reversalPa}
        />
      )}
    </Box>
  );
};

export default WorkspaceDetailPage;
